import { VDataTable } from "vuetify/components";
import { ref, onMounted, computed } from "vue";
import Draggable from "vuedraggable";

export default {
  name: "TaxonomyEditor",
  components: {
    VDataTable,
    Draggable,
  },
  data() {
    return {
      loadVersion: null,
      showBackupVersions: false,
      hasUnsavedChanges: false,
      trackChanges: false,
      saveVersion: "",
      _versionDisplayMap: {}, 
      versionTypes: {
        PRODUCTION: "production",
        TEST: "test",
        WORKING: "Working Version",
        QA: "QA Version",
      },
      versionDescription: "",
      versionTags: [],
      isVersionSelectorExpanded: false,
      showWorkingVersions: true,
      showEnvironmentVersions: true,
      currentVersionDescription: "",
      taxonomies: [],
      currentView: "attributeManagement",
      selectedTaxonomy: "",
      selectedTopLevelNode: "",
      taxonomyData: {},
      attributeData: {},
      topLevelNodes: [],
      treeItems: [],
      keysToSkip: ["name", "url", "total_items", "attribute_ids"],
      nameDialog: false, 
      newAttributeName: "", 
      publishDialog: false,


      searchQuery: "",

      
      langVariantSearch: "",

      selectedNode: null,
      selectedNodeId: null,
      expandedFilters: [],
      expandedCategories: {},
      leftPaneWidth: 800,
      isDragging: false,
      startX: 0,
      startWidth: 0,

      horizontal: true,
      collapsable: true,

      nodeIdMap: {},
      nodeIdsByType: {},
      editableFilters: {},
      editableFilterNames: {},
      newFilterName: "",
      newFilterValues: {},
      editingFilterName: null,
      editingValue: { filter: null, index: null },
      valueContextMenu: {
        show: false,
        x: 0,
        y: 0,
        attrId: null,
        value: null,
        valueIndex: null,
      },

      customMenus: [
        { name: "Copy Text", command: "copy" },
        { name: "Add Node", command: "add" },
        { name: "Edit Node", command: "edit" },
        { name: "Delete Node", command: "delete" },
      ],

      snackbar: { show: false, message: "", color: "success" },

      FUNCTION_URL: "https://data-reviewer-844472556697.us-central1.run.app", // replace with your actual function URL
      cachedTaxonomies: {},

      
      expandedNodes: new Set(),

      editNodeDialog: false,
      editNodeId: null,
      editNodeName: "",

      allFiltersMap: {},
      allFilterNames: [],
      addExistingFilterName: "",

      sourceColors: {
        base_source: "#F59E0B",
        about_you: "#3B82F6",
        zalando: "#EF4444",
        galaxus_fashion: "#10B981",
        ochsner: "#00CED1",
        other_source: "#9D4EDD",
      },

      attributeSort: "display_name",
      newAttributeValue: {},

      
      attributeTableHeaders: [
        { title: "ID", key: "attribute_id" },
        { title: "Name", key: "name" },
        { title: "Display Name", key: "display_name" },
        { title: "Description", key: "description" },
        { title: "Values", key: "values" },
        { title: "Attribute Group", key: "attribute_group" },
        { title: "Data Type", key: "data_type" },
        { title: "Booleans (1)", key: "booleans_group_1" },
        { title: "Booleans (2)", key: "booleans_group_2" },
        { title: "Booleans (3)", key: "booleans_group_3" },
        { title: "Sort Order", key: "sort_order" },
        { title: "Attribute Level", key: "attribute_level" },
        { title: "Attribute Unit", key: "attribute_unit" },
        { title: "Notes", key: "notes" },
        { title: "Attribute Sources", key: "filter_sources" },
        { title: "Categories", key: "categories" },
        { title: "Actions", key: "actions", width: 60 },
      ],

      
      langVariantHeaders: [
        { text: "ID", value: "attribute_id" },
        { text: "English Name", value: "display_name" },
        { text: "# Values", value: "value_count" },
        { text: "Actions", value: "actions", width: 100 },
      ],

      textEditDialog: {
        show: false,
        attributeObj: null,
        fieldName: "",
        tempValue: "",
      },

      
      valueNotesDialog: {
        show: false,
        attrId: null,
        value: null,
        tempNote: "",
      },

      
      translationsDialog: {
        show: false,
        item: null,
      },

      
      versions: [],
      
      selectedVersion: "",

      
      isAttributeEditorFullscreen: false,
      
      expandedAttributeValues: {},
    };
  },
  computed: {
    formattedTaxonomies() {
      
      
      return (this.taxonomies || []).map((f) => ({
        filename: f, 
        
        displayName: f.replace(/\.json$/, ""),
      }));
    },
    formattedVersions() {
      
      const specialVersions = [];

      
      const prod = this.versions.find((v) => v.toLowerCase() === "production");
      const test = this.versions.find((v) => v.toLowerCase() === "test");
      if (prod) specialVersions.push("Production");
      if (test) specialVersions.push("Test");

      
      const working = this.versions.find((v) => v === "Working Version");
      const qa = this.versions.find((v) => v === "QA Version");
      if (working) specialVersions.push("Working Version");
      if (qa) specialVersions.push("QA Version");

      
      let customVersions = this.versions.filter(
        (v) =>
          !["production", "test", "Working Version", "QA Version"].includes(v)
      );

      
      const formattedCustomVersions = [];

      
      this._versionDisplayMap = {};

      customVersions.forEach((version) => {
        
        const workingBackupMatch = version.match(
          /^Working Version Backup (.+)$/
        );
        const testBackupMatch = version.match(/^test[_ ]backup[_ ](.+)$/i); 
        const prodBackupMatch = version.match(
          /^production[_ ]backup[_ ](.+)$/i
        ); 

        if (workingBackupMatch || testBackupMatch || prodBackupMatch) {
          try {
            
            let backupType, timestampStr;

            if (workingBackupMatch) {
              backupType = "Working Version";
              timestampStr = workingBackupMatch[1];
            } else if (testBackupMatch) {
              backupType = "Test";
              timestampStr = testBackupMatch[1];
            } else {
              backupType = "Production";
              timestampStr = prodBackupMatch[1];
            }

            
            const match = timestampStr.match(
              /^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z$/
            );

            if (match) {
              const [_, year, month, day, hour, minute, second, ms] = match;

              
              const dateStr = `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}Z`;
              const date = new Date(dateStr);

              
              if (!isNaN(date.getTime())) {
                
                const localTimeStr = date.toLocaleString();
                const displayVersion = `${backupType} Backup (${localTimeStr})`;

                
                this._versionDisplayMap[displayVersion] = version;

                formattedCustomVersions.push(displayVersion);
              } else {
                
                formattedCustomVersions.push(version);
              }
            } else {
              
              
              const date = new Date(timestampStr);

              if (!isNaN(date.getTime())) {
                const localTimeStr = date.toLocaleString();
                const displayVersion = `${backupType} Backup (${localTimeStr})`;

                this._versionDisplayMap[displayVersion] = version;
                formattedCustomVersions.push(displayVersion);
              } else {
                formattedCustomVersions.push(version);
              }
            }
          } catch (e) {
            console.error("Error parsing timestamp:", e, version);
            formattedCustomVersions.push(version);
          }
        } else {
          formattedCustomVersions.push(version);
        }
      });

      
      let displayCustomVersions = formattedCustomVersions;
      if (!this.showBackupVersions) {
        displayCustomVersions = displayCustomVersions.filter(
          (v) =>
            !(
              v.includes("Working Version Backup (") ||
              v.includes("Test Backup (") ||
              v.includes("Production Backup (")
            )
        );
      }

      
      const allVersions = [...specialVersions, ...displayCustomVersions];

      console.log("formattedVersions output:", allVersions);
      return allVersions;
    },
    currentVersionLabel() {
      if (!this.loadedVersion) return "Unsaved";
      if (this.loadedVersion.toLowerCase() === "production")
        return "Production";
      if (this.loadedVersion.toLowerCase() === "test") return "Test";
      if (this.loadedVersion === "Working Version") return "Working Version";
      if (this.loadedVersion === "QA Version") return "QA Version";
      return this.loadedVersion;
    },
    orgData() {
      
      if (this.treeItems && this.treeItems.length > 0) {
        return this.applyExpansionState(this.treeItems[0]);
      }
      return { label: "No Data", children: [] };
    },
    showFilters() {
      return (
        this.selectedNode &&
        this.selectedNode.filters &&
        Object.keys(this.selectedNode.filters).length > 0
      );
    },
    sortedAttributes() {
      const arr = Object.entries(this.attributeData).map(([key, rawObj]) => {
        if (!rawObj.attribute_id) {
          rawObj.attribute_id = parseInt(key, 10);
        }
        
        rawObj.valuesArray = this.getValuesArray(rawObj);
        return rawObj;
      });
      const created = arr.filter((a) => a.justCreated);
      const rest = arr.filter((a) => !a.justCreated);
      if (this.attributeSort === "display_name") {
        
        rest.sort((a, b) =>
          (this.getDisplayName(a) || "").localeCompare(
            this.getDisplayName(b) || ""
          )
        );
      } else if (this.attributeSort === "attribute_id") {
        rest.sort((a, b) => a.attribute_id - b.attribute_id);
      }
      return [...created, ...rest];
    },
    filteredAttributes() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      let arr = this.sortedAttributes;
      if (q) {
        arr = arr.filter((item) => {
          const nameMatch = (item.name || "").toLowerCase().includes(q);
          
          const displayNameMatch = this.getDisplayName(item)
            .toLowerCase()
            .includes(q);
          const idMatch = String(item.attribute_id).includes(q);
          return nameMatch || displayNameMatch || idMatch;
        });
      }
      return arr;
    },
    attributeOverviewStats() {
      const stats = {
        totalAttributes: 0,
        nameCounts: {},
        categoryCounts: {},
        avgAttrsPerCategory: 0,
        avgCatsPerAttribute: 0,
        avgValuesPerFilter: 0,
      };
      const allAttrIds = Object.keys(this.attributeData);
      stats.totalAttributes = allAttrIds.length;
      let totalCategoryRefs = 0;
      let totalValues = 0;
      for (const attrId of allAttrIds) {
        const att = this.attributeData[attrId];
        const attName = att.name || "(unnamed)";
        if (!stats.nameCounts[attName]) {
          stats.nameCounts[attName] = 0;
        }
        stats.nameCounts[attName]++;
        if (Array.isArray(att.categories)) {
          for (const cat of att.categories) {
            if (!stats.categoryCounts[cat]) {
              stats.categoryCounts[cat] = 0;
            }
            stats.categoryCounts[cat]++;
            totalCategoryRefs++;
          }
        }
        if (Array.isArray(att.values)) {
          totalValues += att.values.length;
        }
      }
      const distinctCategoryKeys = Object.keys(stats.categoryCounts);
      const numCategories = distinctCategoryKeys.length;
      if (numCategories > 0) {
        stats.avgAttrsPerCategory = (totalCategoryRefs / numCategories).toFixed(
          2
        );
      }
      if (stats.totalAttributes > 0) {
        stats.avgCatsPerAttribute = (
          totalCategoryRefs / stats.totalAttributes
        ).toFixed(2);
        stats.avgValuesPerFilter = (
          totalValues / stats.totalAttributes
        ).toFixed(2);
      }
      return stats;
    },
    sortedNameEntries() {
      const entries = Object.entries(this.attributeOverviewStats.nameCounts);
      entries.sort((a, b) => a[0].localeCompare(b[0]));
      return entries;
    },
    languageVariantAttributes() {
      return this.sortedAttributes.map((attr) => {
        const enLength = this.getValuesArray(attr).length || 0;
        if (!attr.translations.value_de) attr.translations.value_de = [];
        if (!attr.translations.value_fr) attr.translations.value_fr = [];
        if (!attr.translations.value_it) attr.translations.value_it = [];

        const syncArray = (arr) => {
          if (arr.length < enLength) {
            for (let i = arr.length; i < enLength; i++) {
              arr.push("");
            }
          } else if (arr.length > enLength) {
            arr.splice(enLength);
          }
        };

        syncArray(attr.translations.value_de);
        syncArray(attr.translations.value_fr);
        syncArray(attr.translations.value_it);

        return attr;
      });
    },
    filteredLanguageVariantAttributes() {
      const q = this.langVariantSearch.toLowerCase().trim();
      if (!q) return this.languageVariantAttributes;

      return this.languageVariantAttributes.filter((item) => {
        const idStr = String(item.attribute_id);
        const displayName = (this.getDisplayName(item) || "").toLowerCase();
        return idStr.includes(q) || displayName.includes(q);
      });
    },
    
    attributeEditorTableHeight() {
      return this.isAttributeEditorFullscreen
        ? "calc(100vh - 230px)" 
        : "calc(100vh - 390px)"; 
    },
  },
  methods: {
    
    normalizeFiltersRecursively(node) {
      if (!node || typeof node !== "object") return;
      if (Array.isArray(node.attribute_ids)) {
        node.filters = [...node.attribute_ids];
      } else if (Array.isArray(node.filters)) {
        node.attribute_ids = [...node.filters];
      }

      
      if (Array.isArray(node.filters)) {
        node.filters = node.filters.filter((id) => this.attributeData[id]);
      }

      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids = node.attribute_ids.filter(
          (id) => this.attributeData[id]
        );
      }

      
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) =>
          this.normalizeFiltersRecursively(sub)
        );
      }

      
      Object.entries(node).forEach(([k, v]) => {
        if (
          [
            "name",
            "filters",
            "attribute_ids",
            "subcategories",
            "sources",
          ].includes(k)
        )
          return;
        if (v && typeof v === "object") this.normalizeFiltersRecursively(v);
      });
    },
    async setWorkingVersion() {
      
      if (!this.selectedTaxonomy) {
        this.showSnackbar("No taxonomy selected", "error");
        return;
      }

      
      this.normalizeDataBeforePublish();

      
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const backupVersionName = `Working Version Backup ${timestamp}`;

      
      try {
        
        await this.saveVersionToCloud(
          backupVersionName,
          `Initial backup created when setting up Working Version on ${new Date().toLocaleString()}`
        );

        
        const workingResult = await this.saveVersionToCloud(
          "Working Version",
          "Main working version"
        );

        
        const qaResult = await this.saveVersionToCloud(
          "QA Version",
          "Quality assurance version"
        );

        if (workingResult && qaResult) {
          this.loadedVersion = "Working Version";
          this.hasUnsavedChanges = false;
          this.showSnackbar(
            "Successfully set up Working and QA Versions",
            "success"
          );

          
          await this.loadTaxonomyVersions();
          this.loadVersion = "Working Version";
        }
      } catch (error) {
        console.error("Failed to set working versions:", error);
        this.showSnackbar(
          "Failed to set working versions: " + error.message,
          "error"
        );
      }
    },
    
    async saveVersionToCloud(versionName, description) {
      const baseFilename = this.selectedTaxonomy.replace(".json", "");
      const pathModified = `allium-ai-manor/taxonomies/${baseFilename}_modified_${versionName}.json`;
      const pathAttributes = `allium-ai-manor/taxonomies/${baseFilename}_attributes_${versionName}.json`;

      
      const metadata = {
        version: versionName,
        description:
          description ||
          `Version ${versionName} created on ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
      };

      
      const payloadMod = {
        path: pathModified,
        data: this.taxonomyData,
        metadata: metadata,
      };

      const payloadAtt = {
        path: pathAttributes,
        data: this.attributeData,
        metadata: metadata,
      };

      
      const [resMod, resAtt] = await Promise.all([
        fetch(`${this.FUNCTION_URL}?action=save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadMod),
        }),
        fetch(`${this.FUNCTION_URL}?action=save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadAtt),
        }),
      ]);

      if (!resMod.ok || !resAtt.ok) {
        throw new Error(
          `Save failed with statuses ${resMod.status}, ${resAtt.status}`
        );
      }

      await Promise.all([resMod.json(), resAtt.json()]);
      return true;
    },
    showValueContextMenu(event, attrId, value, valueIndex = null) {
      
      if (this.currentView === "attributeManagement") {
        return;
      }

      
      event.preventDefault();
      this.valueContextMenu.x = event.clientX;
      this.valueContextMenu.y = event.clientY;
      this.valueContextMenu.attrId = attrId;
      this.valueContextMenu.value = value;
      this.valueContextMenu.valueIndex = valueIndex;
      this.valueContextMenu.show = true;

      setTimeout(() => {
        document.addEventListener("click", this.hideValueContextMenu, {
          once: true,
        });
      }, 0);
    },

    hideValueContextMenu() {
      this.valueContextMenu.show = false;
    },

    renameValueFromContextMenu() {
      
      if (this.valueContextMenu.attrId && this.valueContextMenu.value) {
        const attrId = this.valueContextMenu.attrId;
        const oldValue = this.valueContextMenu.value;

        
        const attr = this.attributeData[attrId];
        if (!attr || !attr.values) return;

        
        const newValue = prompt("Enter new value:", oldValue);
        if (!newValue || newValue === oldValue) return;

        
        
        let updated = false;
        let valueMetadata = {};

        for (const valueKey in attr.values) {
          if (attr.values[valueKey].value_en === oldValue) {
            
            valueMetadata = {
              value_de: attr.values[valueKey].value_de || "",
              value_fr: attr.values[valueKey].value_fr || "",
              value_it: attr.values[valueKey].value_it || "",
              value_sort_index: attr.values[valueKey].value_sort_index || 0,
              notes: attr.values[valueKey].notes || "",
              sources: Array.isArray(attr.values[valueKey].sources)
                ? [...attr.values[valueKey].sources]
                : [],
            };

            
            attr.values[valueKey].value_en = newValue;
            updated = true;
          }
        }

        
        if (attr.valuesArray) {
          const valueObj = attr.valuesArray.find(
            (v) => v.value_en === oldValue
          );
          if (valueObj) {
            
            const origSortIndex = valueObj.value_sort_index;

            
            valueObj.value_en = newValue;

            
            if (valueMetadata.value_de)
              valueObj.value_de = valueMetadata.value_de;
            if (valueMetadata.value_fr)
              valueObj.value_fr = valueMetadata.value_fr;
            if (valueMetadata.value_it)
              valueObj.value_it = valueMetadata.value_it;

            
            valueObj.value_sort_index =
              origSortIndex !== undefined
                ? origSortIndex
                : valueMetadata.value_sort_index;
          }
        }

        if (updated) {
          this.hasUnsavedChanges = true;
          this.showSnackbar(
            `Value renamed from "${oldValue}" to "${newValue}"`,
            "success"
          );
        }
      }
      
      else if (this.valueContextMenu.attrId && this.editableFilters) {
        const filterName = this.valueContextMenu.attrId; 
        const valueIndex = this.valueContextMenu.valueIndex;
        const oldValue = this.valueContextMenu.value;

        if (!this.editableFilters[filterName] || valueIndex === null) return;

        const newValue = prompt("Enter new value:", oldValue);
        if (!newValue || newValue === oldValue) return;

        
        this.editableFilters[filterName][valueIndex] = newValue;

        
        const attrId = this.findAttributeIdByDisplayName(filterName);
        if (attrId !== null) {
          const attr = this.attributeData[attrId];
          if (attr && attr.values) {
            
            let valueMetadata = {};

            
            for (const valueKey in attr.values) {
              if (attr.values[valueKey].value_en === oldValue) {
                
                valueMetadata = {
                  value_de: attr.values[valueKey].value_de || "",
                  value_fr: attr.values[valueKey].value_fr || "",
                  value_it: attr.values[valueKey].value_it || "",
                  value_sort_index: attr.values[valueKey].value_sort_index || 0,
                  notes: attr.values[valueKey].notes || "",
                  sources: Array.isArray(attr.values[valueKey].sources)
                    ? [...attr.values[valueKey].sources]
                    : [],
                };

                
                attr.values[valueKey].value_en = newValue;
              }
            }

            
            if (attr.valuesArray) {
              const valueObj = attr.valuesArray.find(
                (v) => v.value_en === oldValue
              );
              if (valueObj) {
                
                const origSortIndex = valueObj.value_sort_index;

                
                valueObj.value_en = newValue;

                
                if (valueMetadata.value_de)
                  valueObj.value_de = valueMetadata.value_de;
                if (valueMetadata.value_fr)
                  valueObj.value_fr = valueMetadata.value_fr;
                if (valueMetadata.value_it)
                  valueObj.value_it = valueMetadata.value_it;

                
                valueObj.value_sort_index =
                  origSortIndex !== undefined
                    ? origSortIndex
                    : valueMetadata.value_sort_index;
              }
            }
          }
        }

        this.hasUnsavedChanges = true;
        this.showSnackbar(
          `Value renamed from "${oldValue}" to "${newValue}"`,
          "success"
        );
      }
    },

    addNotesFromContextMenu() {
      if (this.valueContextMenu.attrId && this.valueContextMenu.value) {
        
        this.openValueNotesDialog(
          this.valueContextMenu.attrId,
          this.valueContextMenu.value
        );
      }
    },
    removeAttributeIdFromAllTopLevels(taxonomyData, attrId, keysToSkip) {
      if (!taxonomyData || typeof taxonomyData !== "object") return;

      
      for (const topLevelKey of Object.keys(taxonomyData)) {
        if (keysToSkip && keysToSkip.includes(topLevelKey)) continue;
        const topLevelObj = taxonomyData[topLevelKey];
        this.removeAttributeIdEverywhere(topLevelObj, attrId, keysToSkip);
      }
    },
    normalizeDataBeforePublish() {
      
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        this.normalizeFiltersRecursively(nodeData);
      }

      
      for (const nodeData of Object.values(this.taxonomyData)) {
        this.cleanupDeletedAttributes(nodeData);
      }

      
      for (const attrId in this.attributeData) {
        const attr = this.attributeData[attrId];

        
        if (typeof attr.display_name === "string") {
          attr.display_name = { en: attr.display_name };
        } else if (!attr.display_name) {
          attr.display_name = { en: "" };
        }

        
        if (!attr.values) attr.values = {};
        if (Array.isArray(attr.values)) {
          const obj = {};
          attr.values.forEach((v, i) => {
            obj[String(i).padStart(8, "0")] = v;
          });
          attr.values = obj;
        }

        
        if (!attr.translations) {
          attr.translations = {
            value_de: [],
            value_fr: [],
            value_it: [],
          };
        }
      }
    },
    cleanupDeletedAttributes(node) {
      if (!node || typeof node !== "object") return;

      
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids = node.attribute_ids.filter(
          (id) => !!this.attributeData[id]
        );
      }

      if (Array.isArray(node.filters)) {
        node.filters = node.filters.filter((id) => !!this.attributeData[id]);
      }

      
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) => this.cleanupDeletedAttributes(sub));
      }

      
      for (const key of Object.keys(node)) {
        if (
          !this.keysToSkip.includes(key) &&
          key !== "filters" &&
          key !== "attribute_ids" &&
          key !== "subcategories" &&
          key !== "sources"
        ) {
          const child = node[key];
          if (child && typeof child === "object") {
            this.cleanupDeletedAttributes(child);
          }
        }
      }
    },
    isAttributeNameUnique(name) {
      return !Object.values(this.attributeData).some(
        (attr) => attr.name === name
      );
    },
    validateVersionName(versionName) {
      if (!versionName) return { valid: true }; 

      
      const lowerName = versionName.toLowerCase();
      const reservedNames = [
        "production",
        "prod",
        "test",
        "testing",
        "staging",
        "working version",
        "qa version",
      ];

      
      if (
        reservedNames.includes(lowerName) ||
        reservedNames.includes(versionName)
      ) {
        return {
          valid: false,
          message: `"${versionName}" is a reserved name. Use appropriate buttons instead.`,
        };
      }

      
      if (
        lowerName.startsWith("prod") ||
        lowerName.startsWith("test") ||
        lowerName.startsWith("stag") ||
        lowerName.startsWith("work") ||
        lowerName.startsWith("qa")
      ) {
        return {
          valid: false,
          message: `Names starting with "${lowerName.substring(
            0,
            4
          )}" are reserved. Choose a different name.`,
        };
      }

      return { valid: true };
    },
    recheckCategories() {
      const attrPathMap = {};

      
      const collectAttributeCategories = (node, currentPath, attrPathMap) => {
        if (!node) return;

        const nodeName = node.name || "Unnamed";
        const path = currentPath ? `${currentPath}/${nodeName}` : nodeName;

        if (Array.isArray(node.filters)) {
          node.filters.forEach((attrId) => {
            const attrIdStr = String(attrId);
            if (!attrPathMap[attrIdStr]) {
              attrPathMap[attrIdStr] = new Set();
            }
            attrPathMap[attrIdStr].add(path);
          });
        }

        if (Array.isArray(node.subcategories)) {
          node.subcategories.forEach((sub) => {
            collectAttributeCategories(sub, path, attrPathMap);
          });
        }
      };

      
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        collectAttributeCategories(nodeData, "", attrPathMap);
      }

      
      for (const [attrIdStr, pathSet] of Object.entries(attrPathMap)) {
        const attrObj = this.attributeData[attrIdStr];
        if (attrObj) {
          attrObj.categories = Array.from(pathSet);
        }
      }

      
      for (const attrIdStr of Object.keys(this.attributeData)) {
        if (!attrPathMap[attrIdStr]) {
          this.attributeData[attrIdStr].categories = [];
        }
      }

      this.showSnackbar("Categories updated for all attributes", "success");
    },
    shouldDisplayCategory(item, index) {
      
      return this.expandedCategories[item.attribute_id] || index < 5;
    },

    toggleExpandCategories(item) {
      const attrId = item.attribute_id;
      this.expandedCategories[attrId] = !this.expandedCategories[attrId];
    },
    async publishTo(target) {
      if (
        !this.selectedTaxonomy ||
        !["production", "test"].includes(target.toLowerCase())
      )
        return;

      const justFilename = this.selectedTaxonomy.replace(".json", "");
      const modPath = `allium-ai-manor/taxonomies/${justFilename}_modified_${target}.json`;
      const attPath = `allium-ai-manor/taxonomies/${justFilename}_attributes_${target}.json`;

      try {
        
        this.normalizeDataBeforePublish();

        
        const taxonomyDataClone = JSON.parse(JSON.stringify(this.taxonomyData));
        const attributeDataClone = JSON.parse(
          JSON.stringify(this.attributeData)
        );

        
        console.log("Publishing taxonomy data...");
        const modResponse = await fetch(`${this.FUNCTION_URL}?action=publish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            path: modPath,
            data: taxonomyDataClone,
            target,
          }),
        });

        if (!modResponse.ok) {
          throw new Error(
            `Failed to publish taxonomy: ${await modResponse.text()}`
          );
        }

        console.log("Publishing attribute data...");
        const attResponse = await fetch(`${this.FUNCTION_URL}?action=publish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            path: attPath,
            data: attributeDataClone,
            target,
          }),
        });

        if (!attResponse.ok) {
          throw new Error(
            `Failed to publish attributes: ${await attResponse.text()}`
          );
        }

        this.showSnackbar(`Published to ${target}`, "success");
        this.publishDialog = false;
        this.loadTaxonomyVersions(); 
      } catch (error) {
        console.error("Error publishing taxonomy:", error);
        this.showSnackbar(`Failed to publish: ${error.message}`, "error");
      }
    },
    openPublishDialog() {
      this.publishDialog = true;
    },
    getDisplayName(attr) {
      if (attr.display_name && typeof attr.display_name === "object") {
        return attr.display_name.en || attr.name || "";
      } else if (typeof attr.display_name === "string") {
        return attr.display_name;
      } else {
        return attr.name || "";
      }
    },
    openNameDialog() {
      console.log("Button clicked, opening dialog");
      this.newAttributeName = "";
      this.nameDialog = true;
      console.log("nameDialog set to", this.nameDialog);
    },
    getValuesArray(attr) {
      try {
        
        if (Array.isArray(attr.values)) {
          return attr.values.map((v, i) => ({
            value_en: typeof v === "string" ? v : v.value_en || "",
            value_sort_index: i,
          }));
        }

        
        const result = [];
        if (attr.values) {
          for (const key in attr.values) {
            if (attr.values.hasOwnProperty(key)) {
              const vo = attr.values[key];
              result.push({
                value_en: vo.value_en || "",
                value_de: vo.value_de || "",
                value_fr: vo.value_fr || "",
                value_it: vo.value_it || "",
                value_sort_index: vo.value_sort_index || 0,
              });
            }
          }
          
          result.sort((a, b) => a.value_sort_index - b.value_sort_index);
        }
        return result;
      } catch (e) {
        console.error("Error in getValuesArray:", e);
        return [];
      }
    },
    nextValueId(attr) {
      
      const historicalIds = attr.historicalValueIds || [];
      
      const currentIds = Object.keys(attr.values || {});
      
      const allIds = [...new Set([...historicalIds, ...currentIds])];

      
      if (allIds.length === 0) {
        return "70000001";
      }

      
      const maxId = Math.max(...allIds.map((id) => parseInt(id, 10)));
      const nextNum = maxId + 1;
      return String(nextNum).padStart(8, "0");
    },
    downloadTaxonomy() {
      
      if (!this.selectedTaxonomy) {
        this.showSnackbar("No taxonomy selected", "warning");
        return;
      }

      
      const baseFilename = this.selectedTaxonomy.replace(".json", "");

      
      const suffix = this.loadVersion.trim()
        ? this.loadVersion.trim()
        : new Date().toISOString().split("T")[0];

      
      const fileNameMod = `${baseFilename}_modified_${suffix}.json`;
      const fileNameAtt = `${baseFilename}_attributes_${suffix}.json`;

      
      const modJson = JSON.stringify(this.taxonomyData, null, 2);
      const attJson = JSON.stringify(this.attributeData, null, 2);

      
      const modBlob = new Blob([modJson], { type: "application/json" });
      const modUrl = URL.createObjectURL(modBlob);
      const modLink = document.createElement("a");
      modLink.href = modUrl;
      modLink.download = fileNameMod;
      document.body.appendChild(modLink);
      modLink.click();
      document.body.removeChild(modLink);
      URL.revokeObjectURL(modUrl);

      
      setTimeout(() => {
        
        const attBlob = new Blob([attJson], { type: "application/json" });
        const attUrl = URL.createObjectURL(attBlob);
        const attLink = document.createElement("a");
        attLink.href = attUrl;
        attLink.download = fileNameAtt;
        document.body.appendChild(attLink);
        attLink.click();
        document.body.removeChild(attLink);
        URL.revokeObjectURL(attUrl);
      }, 300);

      
      this.showSnackbar("Taxonomy + Attributes downloaded", "success");
    },
    
    flattenCategoryTree(node, pathSoFar, outputArr) {
      const name = node.name || "Unnamed";
      
      const localPath = [...pathSoFar, name];

      
      const pathString = localPath.join("|");

      
      const [level1, level2, level3, level4, level5] = localPath;

      
      outputArr.push({
        CategoryPath: pathString,
        CategoryLevel1: level1 || "",
        CategoryLevel2: level2 || "",
        CategoryLevel3: level3 || "",
        CategoryLevel4: level4 || "",
        CategoryLevel5: level5 || "",
      });

      
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) => {
          this.flattenCategoryTree(sub, localPath, outputArr);
        });
      } else {
        
        
        const keys = Object.keys(node).filter((k) => {
          return (
            !this.keysToSkip.includes(k) &&
            k !== "filters" &&
            k !== "subcategories" &&
            k !== "sources"
          );
        });
        for (const k of keys) {
          const child = node[k];
          if (child && typeof child === "object") {
            this.flattenCategoryTree(child, localPath, outputArr);
          }
        }
      }
    },
    exportCategoriesToCSV() {
      
      const flattened = [];
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        if (!nodeData) continue;
        
        this.flattenCategoryTree(nodeData, [], flattened);
      }

      
      
      const headers = [
        "CategoryPath",
        "Category Level 1",
        "Category Level 2",
        "Category Level 3",
        "Category Level 4",
        "Category Level 5",
      ];
      const escapeForCSV = (val) => {
        if (val == null) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      };

      
      let csvContent = headers.join(",") + "\n";

      
      flattened.forEach((item) => {
        const row = [
          item.CategoryPath,
          item.CategoryLevel1,
          item.CategoryLevel2,
          item.CategoryLevel3,
          item.CategoryLevel4,
          item.CategoryLevel5,
        ]
          .map(escapeForCSV)
          .join(",");
        csvContent += row + "\n";
      });

      
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "categories_export.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSnackbar("Categories CSV exported successfully!", "success");
    },
    addAttributeValue(item) {
      const txt = (this.newAttributeValue[item.attribute_id] || "").trim();
      if (!txt) return;

      
      const valId = this.nextValueId(item);

      
      if (!item.values) item.values = {};

      
      item.values[valId] = {
        value_en: txt,
        value_de: "",
        value_fr: "",
        value_it: "",
        value_sort_index: Object.keys(item.values).length,
        notes: "",
        sources: [],
      };

      
      if (!item.historicalValueIds) {
        item.historicalValueIds = [];
      }
      item.historicalValueIds.push(valId);

      
      item.valuesArray = this.getValuesArray(item);

      
      this.newAttributeValue[item.attribute_id] = "";
    },

    removeAttributeValue(item, index) {
      
      const valObj = item.valuesArray[index];
      const id = Object.keys(item.values).find(
        (k) => item.values[k] === valObj
      );
      if (id) {
        delete item.values[id];
      }

      
      const remainingValues = Object.values(item.values);
      remainingValues.sort((a, b) => a.value_sort_index - b.value_sort_index);
      remainingValues.forEach((vo, newIndex) => {
        vo.value_sort_index = newIndex;
      });

      
      item.valuesArray = remainingValues;
    },
    onSearch() {
      console.log("Search triggered:", this.searchQuery);
    },
    nodeDataSources(node) {
      const originalData = this.nodeIdMap[node.id];
      return originalData?.sources?.category ?? [];
    },
    getSourceChipStyle(src) {
      const color = this.sourceColors[src] || "#AAA";
      return {
        backgroundColor: color,
        color: "#fff",
      };
    },
    getFilterNameSourcesForSelectedNode(filterName) {
      if (!this.selectedNodeId) return [];

      
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (nodeData?.sources?.filters?.[filterName]?.filter_sources) {
        return nodeData.sources.filters[filterName].filter_sources;
      }

      
      const attrId = this.findAttributeIdByDisplayName(filterName);
      if (attrId !== null && this.attributeData[attrId]) {
        
        if (Array.isArray(this.attributeData[attrId].filter_sources)) {
          return this.attributeData[attrId].filter_sources;
        }
        
        if (Array.isArray(this.attributeData[attrId].sources)) {
          return this.attributeData[attrId].sources;
        }
      }

      return [];
    },
    getValueChipStyle(filterName, val) {
      try {
        
        const sources = this.getFilterValueSourcesForSelectedNode(
          filterName,
          val
        );

        if (!sources || sources.length === 0) {
          return {
            backgroundColor: "#f5f5f5",
            color: "#000",
          };
        }

        const firstSource = sources[0];
        const sourceColors = this.sourceColors;
        const color = sourceColors[firstSource] || "#AAA";

        return {
          backgroundColor: color,
          color: "#fff",
        };
      } catch (e) {
        console.error("Error in getValueChipStyle:", e);
        return {
          backgroundColor: "#f5f5f5",
          color: "#000",
        };
      }
    },
    getFilterValueSourcesForSelectedNode(filterName, filterValue) {
      if (!this.selectedNodeId) return [];

      try {
        
        const nodeId = this.selectedNodeId;
        const nodeMap = this.nodeIdMap;
        const nodeData = nodeMap[nodeId];

        
        if (
          nodeData &&
          nodeData.sources &&
          nodeData.sources.filters &&
          nodeData.sources.filters[filterName] &&
          nodeData.sources.filters[filterName].values &&
          nodeData.sources.filters[filterName].values[filterValue]
        ) {
          
          return Array.from(
            nodeData.sources.filters[filterName].values[filterValue]
          );
        }

        
        
        let attrId = null;
        for (const id in this.attributeData) {
          const attr = this.attributeData[id];
          const displayName =
            attr.display_name && typeof attr.display_name === "object"
              ? attr.display_name.en
              : typeof attr.display_name === "string"
              ? attr.display_name
              : attr.name;
          if (displayName === filterName) {
            attrId = parseInt(id, 10);
            break;
          }
        }

        if (attrId !== null) {
          const attr = this.attributeData[attrId];
          if (attr && attr.values) {
            
            for (const valueId in attr.values) {
              if (attr.values[valueId].value_en === filterValue) {
                return attr.values[valueId].sources
                  ? Array.from(attr.values[valueId].sources)
                  : [];
              }
            }
          }
        }

        return [];
      } catch (e) {
        console.error("Error in getFilterValueSourcesForSelectedNode:", e);
        return [];
      }
    },
    getAttributeEditorValueChipStyle(attrId, val) {
      try {
        const attrObj = this.attributeData[attrId];
        if (!attrObj || !attrObj.values) {
          return {
            backgroundColor: "#f5f5f5",
            color: "#000",
          };
        }

        let sources = null;
        
        for (const key in attrObj.values) {
          const valObj = attrObj.values[key];
          if (valObj.value_en === val) {
            sources = valObj.sources;
            break;
          }
        }

        if (!sources || sources.length === 0) {
          return {
            backgroundColor: "#f5f5f5",
            color: "#000",
          };
        }

        const color = this.sourceColors[sources[0]] || "#AAA";
        return {
          backgroundColor: color,
          color: "#fff",
        };
      } catch (e) {
        console.error("Error in getAttributeEditorValueChipStyle:", e);
        return {
          backgroundColor: "#f5f5f5",
          color: "#000",
        };
      }
    },
    hasValueNote(attrId, val) {
      const attr = this.attributeData[attrId];
      if (!attr) return false;
      if (!attr.value_notes) return false;
      const note = attr.value_notes[val];
      return note && note.trim().length > 0;
    },
    loadTaxonomyList() {
      const fetchUrl = this.getFetchUrl("taxonomies/index.json");
      fetch(fetchUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          this.taxonomies = data;
          if (data.length > 0) {
            this.selectedTaxonomy = data[0];
          }
        })
        .catch((error) => {
          console.error("Error fetching taxonomy list:", error);
          this.showSnackbar("Failed to load taxonomy list", "error");
        });
    },
    getFetchUrl(filename) {
      
      return process.env.NODE_ENV === "production"
        ? `/manor/${filename}`
        : `/${filename}`;
    },
    async loadTaxonomyVersions() {
      if (!this.selectedTaxonomy) return Promise.resolve([]);
      const justFilename = this.selectedTaxonomy.replace(".json", "");
      const url = `${this.FUNCTION_URL}?action=listVersions&filename=${justFilename}`;

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch versions");
        const data = await res.json();
        let allVersions = data.versions || [];

        
        const hasProduction = allVersions.some(
          (v) => v.toLowerCase() === "production"
        );
        const hasTest = allVersions.some((v) => v.toLowerCase() === "test");
        const hasWorking = allVersions.some((v) => v === "Working Version");
        const hasQA = allVersions.some((v) => v === "QA Version");

        
        let orderedVersions = [];

        
        if (hasWorking) {
          orderedVersions.push("Working Version");
        }

        
        if (hasProduction) {
          orderedVersions.push(
            allVersions.find((v) => v.toLowerCase() === "production")
          );
        }

        
        if (hasQA) {
          orderedVersions.push("QA Version");
        }

        
        if (hasTest) {
          orderedVersions.push(
            allVersions.find((v) => v.toLowerCase() === "test")
          );
        }

        
        const otherVersions = allVersions.filter(
          (v) =>
            v.toLowerCase() !== "production" &&
            v.toLowerCase() !== "test" &&
            v !== "Working Version" &&
            v !== "QA Version"
        );

        orderedVersions = [...orderedVersions, ...otherVersions];

        this.versions = orderedVersions;
        return this.versions;
      } catch (err) {
        console.error("Error fetching versions:", err);
        this.showSnackbar("Failed to list versions", "error");
        this.versions = [];
        return [];
      }
    },
    async loadTaxonomyVersion(version) {
      if (!this.selectedTaxonomy || !version) return;
      const justFilename = this.selectedTaxonomy.replace(".json", "");
      const apiUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${justFilename}_modified_${version}.json`;

      try {
        const resMod = await fetch(apiUrl);
        if (!resMod.ok)
          throw new Error(
            `Failed to load modified taxonomy for version ${version}`
          );
        const modifiedData = await resMod.json();

        const attUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${justFilename}_attributes_${version}.json`;
        const resAtt = await fetch(attUrl);
        if (!resAtt.ok)
          throw new Error(`Failed to load attributes for version ${version}`);
        const attributesData = await resAtt.json();

        
        this.setModifiedTaxonomyData(modifiedData, attributesData);
        this.loadVersion = version; 
        this.showSnackbar(`Loaded taxonomy version: ${version}`, "info");
      } catch (error) {
        console.error("Error loading taxonomy version:", error);
        this.showSnackbar("Failed to load taxonomy version", "error");
      }
    },
    onVersionSelected() {
      this.loadTaxonomyFromCloud();
    },
    onLoadVersionChange(selected) {
      console.log("Selected version:", selected);

      
      if (!selected) {
        
        this.loadVersion = this.loadedVersion || null;
        return;
      }

      
      if (
        (selected.includes("Working Version Backup (") ||
          selected.includes("Test Backup (") ||
          selected.includes("Production Backup (")) &&
        this._versionDisplayMap
      ) {
        
        const actualVersion = this._versionDisplayMap[selected];
        if (actualVersion) {
          console.log("Loading actual version:", actualVersion);
          selected = actualVersion;
        }
      }

      
      let versionToLoad = selected;
      if (selected === "Production") versionToLoad = "production";
      if (selected === "Test") versionToLoad = "test";

      this.loadTaxonomyFromCloud(versionToLoad);
    },
    async loadTaxonomyFromCloud(versionToLoad) {
      console.log("loadTaxonomyFromCloud called with:", versionToLoad);

      if (!this.selectedTaxonomy) return;

      
      if (!versionToLoad && !this.loadedVersion) {
        const workingExists = this.versions.find(
          (v) => v === "Working Version"
        );
        versionToLoad = workingExists ? "Working Version" : "production";
      }
      
      else if (!versionToLoad && this.loadedVersion) {
        versionToLoad = this.loadedVersion;
      }

      
      const version =
        typeof versionToLoad === "string" && versionToLoad
          ? versionToLoad
          : this.loadVersion || this.loadedVersion || "Working Version";

      console.log("Final version to use:", version);

      const justFilename = this.selectedTaxonomy.replace(".json", "");

      
      const trimmedVersion = version.trim();

      
      const normalizedSlug = ["production", "test"].includes(
        trimmedVersion.toLowerCase()
      )
        ? trimmedVersion.toLowerCase()
        : trimmedVersion;

      console.log("Normalized version slug:", normalizedSlug);

      const modFile = normalizedSlug
        ? `${justFilename}_modified_${normalizedSlug}.json`
        : `${justFilename}_modified.json`;
      const attFile = normalizedSlug
        ? `${justFilename}_attributes_${normalizedSlug}.json`
        : `${justFilename}_attributes.json`;

      this.tryRemoteLoad(justFilename, modFile, attFile, normalizedSlug).then(
        ({ loaded, handled }) => {
          if (!loaded && !handled) {
            
            console.log("Remote load failed, falling back to local");
            this.loadTaxonomyData();
          }
        }
      );
    },
    async tryRemoteLoad(baseName, modName, attName, versionSlug) {
      const modUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${modName}`;
      const attUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${attName}`;

      
      this.trackChanges = false;
      this.hasUnsavedChanges = false;

      try {
        
        const [modResponse, attResponse] = await Promise.all([
          fetch(modUrl).then((res) =>
            res.ok
              ? res.json()
              : Promise.reject(`Modified taxonomy fetch failed: ${res.status}`)
          ),
          fetch(attUrl).then((res) =>
            res.ok
              ? res.json()
              : Promise.reject(`Attributes fetch failed: ${res.status}`)
          ),
        ]);

        
        const modifiedData = modResponse.data;
        const attributesData = attResponse.data;
        const metadata = modResponse.metadata || {};

        
        this.selectedNode = null;
        this.selectedNodeId = null;
        this.expandedFilters = [];

        
        this.taxonomyData = modifiedData;
        this.attributeData = attributesData;

        
        Object.values(this.taxonomyData).forEach((node) =>
          this.normalizeFiltersRecursively(node)
        );

        
        for (const attr of Object.values(this.attributeData)) {
          
          if (typeof attr.display_name === "string") {
            attr.display_name = { en: attr.display_name };
          } else if (
            !attr.display_name ||
            typeof attr.display_name !== "object"
          ) {
            attr.display_name = { en: "" };
          }

          
          if (Array.isArray(attr.values)) {
            const obj = {};
            attr.values.forEach((v, i) => {
              obj[String(i).padStart(8, "0")] = v;
            });
            attr.values = obj;
          } else if (!attr.values) {
            attr.values = {};
          }

          let idx = 0;
          for (const [id, v] of Object.entries(attr.values)) {
            const vo = typeof v === "string" ? { value_en: v } : v;
            attr.values[id] = {
              value_en: vo.value_en ?? vo.en ?? "",
              value_de: vo.value_de ?? vo.de ?? "",
              value_fr: vo.value_fr ?? vo.fr ?? "",
              value_it: vo.value_it ?? vo.it ?? "",
              notes: vo.notes ?? "",
              sources: Array.isArray(vo.sources) ? vo.sources : [],
              value_sort_index: vo.value_sort_index ?? idx,
            };
            idx++;
          }

          
          if (!attr.historicalValueIds) {
            attr.historicalValueIds = Object.keys(attr.values || {});
          }

          
          if (!attr.translations) {
            attr.translations = {
              value_de: [],
              value_fr: [],
              value_it: [],
            };
          } else {
            if (!Array.isArray(attr.translations.value_de))
              attr.translations.value_de = [];
            if (!Array.isArray(attr.translations.value_fr))
              attr.translations.value_fr = [];
            if (!Array.isArray(attr.translations.value_it))
              attr.translations.value_it = [];
          }

          attr.valuesArray = this.getValuesArray(attr);
        }

        
        if (!this.taxonomyData._metadata) {
          this.taxonomyData._metadata = {};
        }
        if (!this.taxonomyData._metadata.historicalAttributeIds) {
          this.taxonomyData._metadata.historicalAttributeIds = Object.keys(
            this.attributeData
          );
        }

        
        this.topLevelNodes = this.getTopLevelNodes(modifiedData);
        if (this.topLevelNodes.length > 0) {
          this.selectedTopLevelNode = this.topLevelNodes[0];
          this.loadTopLevelNode();
        } else {
          this.treeItems = [];
        }
        this.gatherAllFilters();

        
        this.currentVersionDescription = metadata.description || "";
        this.loadedVersion = versionSlug;
        this.saveVersion = !["production", "test"].includes(
          versionSlug.toLowerCase()
        )
          ? versionSlug
          : "";

        
        setTimeout(() => {
          this.trackChanges = true;
        }, 500);

        
        this.cachedTaxonomies[baseName] = {
          modified: modifiedData,
          attributes: attributesData,
        };

        this.showSnackbar(
          `Loaded taxonomy (${versionSlug || "default"}) from cloud`,
          "success"
        );
        return { loaded: true, handled: true };
      } catch (error) {
        console.error("Error in tryRemoteLoad:", error);
        if (["production", "test"].includes(versionSlug)) {
          this.showSnackbar(
            `Failed to load ${versionSlug} version: ${error}`,
            "error"
          );
          return { loaded: false, handled: true };
        }
        return { loaded: false, handled: false };
      }
    },
    loadTaxonomyData() {
      if (!this.selectedTaxonomy) return;
      const justFilename = this.selectedTaxonomy.replace(".json", "");
      const modUrl = this.getFetchUrl(
        `taxonomies/${justFilename}_modified.json`
      );
      const attUrl = this.getFetchUrl(
        `taxonomies/${justFilename}_attributes.json`
      );
      Promise.all([fetch(modUrl), fetch(attUrl)])
        .then(([resMod, resAtt]) => {
          if (!resMod.ok) {
            throw new Error(
              `Failed to load modified taxonomy. Status: ${resMod.status}`
            );
          }
          if (!resAtt.ok) {
            throw new Error(
              `Failed to load attributes object. Status: ${resAtt.status}`
            );
          }
          return Promise.all([resMod.json(), resAtt.json()]);
        })
        .then(([modifiedData, attributesData]) => {
          this.setModifiedTaxonomyData(modifiedData, attributesData);
          this.saveVersion = ""; 
          this.loadedVersion = ""; 
          this.currentVersionDescription = ""; 
          this.hasUnsavedChanges = false; 
          this.showSnackbar("Loaded taxonomy from local", "info");
        })
        .catch((error) => {
          console.error("Error fetching taxonomy data:", error);
          this.showSnackbar("Failed to load taxonomy", "error");
        });
    },
    setModifiedTaxonomyData(modData, attData) {
      
      this.selectedNode = null;
      this.selectedNodeId = null;
      this.expandedFilters = [];

      
      this.taxonomyData = modData;
      Object.values(this.taxonomyData).forEach((node) =>
        this.normalizeFiltersRecursively(node)
      );
      this.attributeData = attData;

      for (const attr of Object.values(this.attributeData)) {
        
        if (typeof attr.display_name === "string") {
          attr.display_name = { en: attr.display_name };
        } else if (
          !attr.display_name ||
          typeof attr.display_name !== "object"
        ) {
          attr.display_name = { en: "" };
        }

        
        if (Array.isArray(attr.values)) {
          const obj = {};
          attr.values.forEach((v, i) => {
            obj[String(i).padStart(8, "0")] = v;
          });
          attr.values = obj;
        } else if (!attr.values) {
          attr.values = {};
        }

        let idx = 0;
        for (const [id, v] of Object.entries(attr.values)) {
          const vo = typeof v === "string" ? { value_en: v } : v;
          attr.values[id] = {
            value_en: vo.value_en ?? vo.en ?? "",
            value_de: vo.value_de ?? vo.de ?? "",
            value_fr: vo.value_fr ?? vo.fr ?? "",
            value_it: vo.value_it ?? vo.it ?? "",
            notes: vo.notes ?? "",
            sources: Array.isArray(vo.sources) ? vo.sources : [],
            value_sort_index: vo.value_sort_index ?? idx,
          };
          idx++;
        }

        
        if (!attr.historicalValueIds) {
          attr.historicalValueIds = Object.keys(attr.values || {});
        }

        
        if (!attr.translations) {
          attr.translations = {
            value_de: [],
            value_fr: [],
            value_it: [],
          };
        } else {
          if (!Array.isArray(attr.translations.value_de))
            attr.translations.value_de = [];
          if (!Array.isArray(attr.translations.value_fr))
            attr.translations.value_fr = [];
          if (!Array.isArray(attr.translations.value_it))
            attr.translations.value_it = [];
        }

        attr.valuesArray = this.getValuesArray(attr);
      }

      
      if (!this.taxonomyData._metadata) {
        this.taxonomyData._metadata = {};
      }
      if (!this.taxonomyData._metadata.historicalAttributeIds) {
        this.taxonomyData._metadata.historicalAttributeIds = Object.keys(
          this.attributeData
        );
      }

      this.topLevelNodes = this.getTopLevelNodes(modData);
      if (this.topLevelNodes.length > 0) {
        this.selectedTopLevelNode = this.topLevelNodes[0];
        this.loadTopLevelNode();
      } else {
        this.treeItems = [];
      }
      this.gatherAllFilters();
    },
    getTopLevelNodes(data) {
      if (!data || typeof data !== "object") return [];
      return Object.keys(data).filter((key) => !this.keysToSkip.includes(key));
    },
    loadTopLevelNode() {
      
      if (
        this.expandedNodes.size === 0 &&
        this.treeItems &&
        this.treeItems.length > 0
      ) {
        
        this.collectExpansions(this.treeItems[0]);
      }

      if (
        this.selectedTopLevelNode &&
        this.taxonomyData[this.selectedTopLevelNode]
      ) {
        const data = this.taxonomyData[this.selectedTopLevelNode];
        this.nodeIdMap = {};
        this.nodeIdsByType = {};
        this.treeItems = this.convertToTreeItems(
          data,
          this.selectedTopLevelNode
        );
        this.buildIdToNodeMap(data, this.selectedTopLevelNode);
      } else {
        this.treeItems = [];
        this.nodeIdMap = {};
        this.nodeIdsByType = {};
      }
    },
    convertToTreeItems(data, parentId = "") {
      if (!data || typeof data !== "object") {
        const label = (data && data.name) || parentId || "New Node";
        return [{ id: parentId, label, children: [], filters: [] }];
      }
      const itemId = parentId || "root";
      const label = data.name || itemId || "New Node";
      const root = {
        id: itemId,
        label,
        children: [],
        filters: Array.isArray(data.attribute_ids) ? data.attribute_ids : [],
      };
      if (root.filters.length > 0) {
        root.style = { background: "#108ffe", color: "#fff" };
      }
      if (Array.isArray(data.subcategories)) {
        data.subcategories.forEach((sub, index) => {
          const subId = `${itemId}.[${index}]`;
          const subItem = this.createItemFromData(sub, subId);
          if (subItem) root.children.push(subItem);
        });
      } else {
        const keys = Object.keys(data).filter(
          (k) =>
            !this.keysToSkip.includes(k) &&
            k !== "filters" &&
            k !== "name" &&
            k !== "subcategories" &&
            k !== "sources"
        );
        keys.forEach((k) => {
          const childId = `${itemId}.${k}`;
          const childData = data[k];
          if (childData && typeof childData === "object") {
            const childItem = this.createItemFromData(childData, childId);
            if (childItem) {
              root.children.push(childItem);
            }
          }
        });
      }
      return [root];
    },
    createItemFromData(data, itemId) {
      if (!data || typeof data !== "object") return null;
      const label = data.name || itemId || "New Node";

      
      const nodeFilters = Array.isArray(data.attribute_ids)
        ? data.attribute_ids
        : Array.isArray(data.filters)
        ? data.filters
        : [];

      const item = {
        id: itemId,
        label,
        children: [],
        filters: nodeFilters,
      };

      if (item.filters.length > 0) {
        item.style = { background: "#108ffe", color: "#fff" };
      }
      if (Array.isArray(data.subcategories)) {
        data.subcategories.forEach((sub, idx) => {
          const childId = `${itemId}.[${idx}]`;
          const childItem = this.createItemFromData(sub, childId);
          if (childItem) item.children.push(childItem);
        });
      } else {
        const keys = Object.keys(data).filter(
          (k) =>
            !this.keysToSkip.includes(k) &&
            k !== "filters" &&
            k !== "name" &&
            k !== "subcategories" &&
            k !== "sources"
        );
        keys.forEach((k) => {
          const childId = `${itemId}.${k}`;
          const childData = data[k];
          if (childData && typeof childData === "object") {
            const childItem = this.createItemFromData(childData, childId);
            if (childItem) {
              item.children.push(childItem);
            }
          }
        });
      }
      return item;
    },
    buildIdToNodeMap(data, parentId = "") {
      if (!data || typeof data !== "object") return;
      const itemId = parentId || "root";
      this.nodeIdMap[itemId] = data;
      if (Array.isArray(data.subcategories)) {
        data.subcategories.forEach((sub, idx) => {
          const subId = `${itemId}.[${idx}]`;
          this.buildIdToNodeMap(sub, subId);
        });
      } else {
        const keys = Object.keys(data).filter(
          (k) =>
            !this.keysToSkip.includes(k) &&
            k !== "filters" &&
            k !== "name" &&
            k !== "subcategories" &&
            k !== "sources"
        );
        keys.forEach((k) => {
          this.buildIdToNodeMap(data[k], `${itemId}.${k}`);
        });
      }
    },
    collectNodeFilterIds(nodeData) {
      const setOfIds = new Set();
      if (!nodeData) return setOfIds;
      if (Array.isArray(nodeData.filters)) {
        nodeData.filters.forEach((fId) => setOfIds.add(fId));
      }
      return setOfIds;
    },
    onNodeClick(e, node) {
      const id = node.id;
      if (!id) {
        this.selectedNode = null;
        this.selectedNodeId = null;
        this.expandedFilters = [];
        return;
      }

      this.selectedNodeId = id;
      const originalData = this.nodeIdMap[id];

      
      const filtersDict = {};
      (originalData?.attribute_ids || originalData?.filters || []).forEach(
        (attrId) => {
          const attObj = this.attributeData[attrId];
          if (!attObj) {
            filtersDict[`Unknown #${attrId}`] = [];
          } else {
            filtersDict[this.getDisplayName(attObj)] = this.getValuesArray(
              attObj
            ).map((vo) => vo.value_en);
          }
        }
      );

      this.selectedNode = {
        name: originalData?.name || node.label,
        filters: filtersDict,
      };
      this.editableFilters = JSON.parse(JSON.stringify(filtersDict));
      this.editableFilterNames = Object.fromEntries(
        Object.keys(filtersDict).map((n) => [n, n])
      );
      this.newFilterName = "";
      this.newFilterValues = {};
      this.expandedFilters = [];
      this.editingFilterName = null;
      this.editingValue = { filter: null, index: null };
    },
    onContextMenuAction({ command, node }) {
      if (command === "add") {
        this.addNode(node.id);
      } else if (command === "delete") {
        this.deleteNode(node.id);
      } else if (command === "edit") {
        this.startEditingNode(node.id);
      } else if (command === "copy") {
        navigator.clipboard.writeText(node.label).catch(() => {});
        this.showSnackbar(`Copied "${node.label}" to clipboard`, "success");
      }
    },
    findParentOfNode(currentNode, targetId) {
      if (!currentNode || !currentNode.children) return null;
      for (let child of currentNode.children) {
        if (child.id === targetId) return currentNode;
        const found = this.findParentOfNode(child, targetId);
        if (found) return found;
      }
      return null;
    },
    saveNodeName() {
      if (!this.editNodeId) {
        this.editNodeDialog = false;
        return;
      }
      const node = this.findTreeItemById(this.treeItems[0], this.editNodeId);
      if (node) {
        node.label = this.editNodeName.trim() || node.label;
        const nodeData = this.nodeIdMap[this.editNodeId];
        if (nodeData) {
          nodeData.name = node.label;
        }
      }
      this.editNodeDialog = false;
      this.showSnackbar("Node renamed successfully", "success");
    },
    /**
     * --------------------------------------------------------------------
     * This method now preserves the current expansion state before
     * rebuilding the tree, so the user sees the same expansions afterward.
     * --------------------------------------------------------------------
     */
    addNode(parentNodeId) {
      
      const savedExpansions = new Set();
      if (this.treeItems && this.treeItems.length > 0) {
        
        this.collectExpansions(this.treeItems[0]);
        
        this.expandedNodes.forEach((id) => savedExpansions.add(id));
      }

      
      const parentData = this.nodeIdMap[parentNodeId];
      if (!parentData) {
        console.error("Parent node not found");
        return;
      }
      const newId = this.getNextAvailableAttributeId();
      const newTaxonomyNode = {
        name: "New Node",
        filters: [],
        subcategories: [],
      };
      if (!parentData.subcategories) {
        parentData.subcategories = [];
      }
      parentData.subcategories.push(newTaxonomyNode);
      
      this.nodeIdMap[newId] = newTaxonomyNode;

      
      this.loadTopLevelNode();

      
      this.expandedNodes = savedExpansions;

      this.showSnackbar("New node added", "success");
    },
    deleteNode(nodeId) {
      const parent = this.findParentOfNode(this.treeItems[0], nodeId);
      if (parent) {
        const index = parent.children.findIndex((child) => child.id === nodeId);
        if (index !== -1) {
          parent.children.splice(index, 1);
          const segments = nodeId.toString().split(".");
          this.removeNodeFromParent(
            this.taxonomyData[this.selectedTopLevelNode],
            segments,
            this.selectedTopLevelNode
          );
          this.showSnackbar("Node deleted successfully", "success");
        }
      } else {
        console.error("Parent node not found for deletion.");
      }
      if (this.selectedNodeId === nodeId) {
        this.selectedNode = null;
        this.selectedNodeId = null;
      }
    },
    findTreeItemById(rootItem, id) {
      if (!rootItem) return null;
      if (rootItem.id === id) return rootItem;
      if (rootItem.children) {
        for (const child of rootItem.children) {
          const found = this.findTreeItemById(child, id);
          if (found) return found;
        }
      }
      return null;
    },
    removeNodeFromParent(currentData, segments, basePath, level = 0) {
      if (!currentData) return;
      if (level === 0 && segments[0] === basePath) {
        segments.shift();
      }
      if (segments.length === 0) {
        return;
      }
      const head = segments[0];
      if (head.startsWith("[") && head.endsWith("]")) {
        const idxStr = head.slice(1, -1);
        const idx = parseInt(idxStr, 10);
        if (segments.length === 1) {
          if (
            Array.isArray(currentData.subcategories) &&
            idx >= 0 &&
            idx < currentData.subcategories.length
          ) {
            currentData.subcategories.splice(idx, 1);
          }
        } else {
          if (
            Array.isArray(currentData.subcategories) &&
            idx >= 0 &&
            idx < currentData.subcategories.length
          ) {
            segments.shift();
            this.removeNodeFromParent(
              currentData.subcategories[idx],
              segments,
              basePath,
              level + 1
            );
          }
        }
      } else {
        if (segments.length === 1) {
          if (head in currentData) {
            delete currentData[head];
          }
        } else {
          const childData = currentData[head];
          segments.shift();
          if (childData && typeof childData === "object") {
            this.removeNodeFromParent(childData, segments, basePath, level + 1);
          }
        }
      }
    },
    startEditingNode(nodeId) {
      const nodeData = this.nodeIdMap[nodeId];
      if (!nodeData) return;
      this.editNodeId = nodeId;
      this.editNodeName = nodeData.name || "";
      this.editNodeDialog = true;
    },
    toggleFilter(filterName) {
      const idx = this.expandedFilters.indexOf(filterName);
      if (idx === -1) {
        this.expandedFilters.push(filterName);
      } else {
        this.expandedFilters.splice(idx, 1);
      }
    },
    addFilter() {
      const name = this.newFilterName.trim();
      if (!name) return;
      if (!this.isAttributeNameUnique(name)) {
        this.showSnackbar(`Attribute name "${name}" already exists`, "error");
        return;
      }

      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) return;

      if (!Array.isArray(nodeData.filters)) {
        nodeData.filters = [];
      }
      if (!Array.isArray(nodeData.attribute_ids)) {
        nodeData.attribute_ids = [];
      }

      const nextNewId = this.getNextAvailableAttributeId();
      nodeData.filters.push(nextNewId);
      nodeData.attribute_ids.push(nextNewId);

      const nodePath = this.getNodePathById(this.selectedNodeId);

      this.attributeData[nextNewId] = {
        name: name,
        display_name: { en: name, de: "", fr: "", it: "" },
        description: "",
        data_type: "text",
        always_required: false,
        is_image_attribute: false,
        is_closed_list: false,
        filter_attribute: false,
        critical_attribute: false,
        secondary_allowed: false,
        customer_facing: true,
        notes: "",
        sort_order: "Alphabetical",
        attribute_level: "Product",
        attribute_unit: "",
        justCreated: true,
        attribute_id: nextNewId,
        is_category_type_attribute: false,
        attribute_group: null,
        values: {},
        historicalValueIds: [],
        categories: nodePath ? [nodePath] : [],
        translations: {
          value_de: [],
          value_fr: [],
          value_it: [],
        },
        sources: [],
      };

      
      if (!this.taxonomyData._metadata) {
        this.taxonomyData._metadata = {};
      }
      if (!this.taxonomyData._metadata.historicalAttributeIds) {
        this.taxonomyData._metadata.historicalAttributeIds = [];
      }
      this.taxonomyData._metadata.historicalAttributeIds.push(nextNewId);

      if (!this.selectedNode.filters) {
        this.selectedNode.filters = {};
      }
      this.selectedNode.filters[name] = [];
      this.editableFilters[name] = [];
      this.editableFilterNames[name] = name;
      this.newFilterName = "";

      if (!this.expandedFilters.includes(name)) {
        this.expandedFilters.push(name);
      }

      const treeItem = this.findTreeItemById(
        this.treeItems[0],
        this.selectedNodeId
      );
      if (treeItem) {
        treeItem.filters = nodeData.filters;
        treeItem.style = { background: "#108ffe", color: "#fff" };
      }

      this.gatherAllFilters();
    },
    findAttributeIdByDisplayName(filterName) {
      
      const displayNameOnly = filterName.split(" [")[0];

      for (const [id, obj] of Object.entries(this.attributeData)) {
        if (
          this.getDisplayName(obj) === displayNameOnly ||
          obj.name === displayNameOnly
        ) {
          return id;
        }
      }
      return null;
    },
    getNextAvailableAttributeId() {
      const historicalIds =
        this.taxonomyData._metadata?.historicalAttributeIds || [];
      if (historicalIds.length === 0) return "1000"; 
      const maxId = Math.max(...historicalIds.map((id) => parseInt(id, 10)));
      return String(maxId + 1);
    },
    removeFilter(filterName) {
      
      delete this.editableFilters[filterName];
      delete this.editableFilterNames[filterName];
      const idx = this.expandedFilters.indexOf(filterName);
      if (idx !== -1) {
        this.expandedFilters.splice(idx, 1);
      }

      
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) return;

      
      const toRemoveId = this.findAttributeIdByDisplayName(filterName);
      if (toRemoveId !== null) {
        
        if (Array.isArray(nodeData.attribute_ids)) {
          nodeData.attribute_ids = nodeData.attribute_ids.filter(
            (id) => String(id) !== String(toRemoveId)
          );
        }

        
        if (Array.isArray(nodeData.filters)) {
          nodeData.filters = nodeData.filters.filter(
            (id) => String(id) !== String(toRemoveId)
          );
        }

        
        const attrObj = this.attributeData[toRemoveId];
        if (attrObj && attrObj.categories) {
          const nodePath = this.getNodePathById(this.selectedNodeId);
          const pathIndex = attrObj.categories.indexOf(nodePath);
          if (pathIndex !== -1) {
            attrObj.categories.splice(pathIndex, 1);
          }
        }
      }

      
      if (this.selectedNode.filters && this.selectedNode.filters[filterName]) {
        delete this.selectedNode.filters[filterName];
      }

      
      this.loadTopLevelNode();
    },
    scanAllFilterIds() {
      const setOfIds = new Set();
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        if (nodeData) {
          this.collectAllFilterIds(nodeData, setOfIds);
        }
      }
      return setOfIds;
    },
    collectAllFilterIds(nodeData, setOfIds = new Set()) {
      if (!nodeData) return setOfIds;
      if (Array.isArray(nodeData.filters)) {
        nodeData.filters.forEach((fId) => setOfIds.add(fId));
      }
      if (Array.isArray(nodeData.subcategories)) {
        nodeData.subcategories.forEach((sub) => {
          if (sub && typeof sub === "object") {
            this.collectAllFilterIds(sub, setOfIds);
          }
        });
      }
      Object.keys(nodeData).forEach((k) => {
        if (
          !this.keysToSkip.includes(k) &&
          k !== "filters" &&
          k !== "name" &&
          k !== "subcategories" &&
          k !== "sources"
        ) {
          const child = nodeData[k];
          if (child && typeof child === "object") {
            this.collectAllFilterIds(child, setOfIds);
          }
        }
      });
      return setOfIds;
    },
    addFilterValue(filterName) {
      const val = (this.newFilterValues[filterName] || "").trim();
      if (!val) return;

      
      this.editableFilters[filterName].push(val);

      const attrId = this.findAttributeIdByDisplayName(filterName);
      if (attrId !== null) {
        const attrObj = this.attributeData[attrId];

        
        const valId = this.nextValueId(attrObj);

        
        const maxIndex = Math.max(
          ...Object.values(attrObj.values || {}).map(
            (v) => v.value_sort_index || 0
          ),
          -1
        );

        
        attrObj.values = attrObj.values || {}; 
        attrObj.values[valId] = {
          value_en: val,
          value_de: "",
          value_fr: "",
          value_it: "",
          value_sort_index: maxIndex + 1,
          notes: "",
          sources: [],
        };

        
        attrObj.historicalValueIds = attrObj.historicalValueIds || [];

        
        if (!attrObj.historicalValueIds.includes(valId)) {
          attrObj.historicalValueIds.push(valId);
        }

        
        if (attrObj.translations) {
          attrObj.translations.value_de = attrObj.translations.value_de || [];
          attrObj.translations.value_fr = attrObj.translations.value_fr || [];
          attrObj.translations.value_it = attrObj.translations.value_it || [];
          attrObj.translations.value_de.push("");
          attrObj.translations.value_fr.push("");
          attrObj.translations.value_it.push("");
        }
      }

      
      this.newFilterValues[filterName] = "";
    },
    removeFilterValue(filterName, idx) {
      const val = this.editableFilters[filterName][idx];
      this.editableFilters[filterName].splice(idx, 1);

      const attrId = this.findAttributeIdByDisplayName(filterName);
      if (attrId === null || !val) return;

      const attrObj = this.attributeData[attrId];
      const keyToDelete = Object.keys(attrObj.values).find(
        (key) => attrObj.values[key].value_en === val
      );
      if (keyToDelete) {
        const valueObj = attrObj.values[keyToDelete];
        const sortIndex = valueObj.value_sort_index;
        delete attrObj.values[keyToDelete];

        

        
        if (
          attrObj.translations?.value_de &&
          sortIndex < attrObj.translations.value_de.length
        ) {
          attrObj.translations.value_de.splice(sortIndex, 1);
        }
        if (
          attrObj.translations?.value_fr &&
          sortIndex < attrObj.translations.value_fr.length
        ) {
          attrObj.translations.value_fr.splice(sortIndex, 1);
        }
        if (
          attrObj.translations?.value_it &&
          sortIndex < attrObj.translations.value_it.length
        ) {
          attrObj.translations.value_it.splice(sortIndex, 1);
        }
      }

      if (attrObj.value_sources) delete attrObj.value_sources[val];
      if (attrObj.value_notes) delete attrObj.value_notes[val];
    },
    getNodePathById(nodeId) {
      if (!this.nodeIdMap || !nodeId) return "";

      const segments = nodeId.split(".");
      let currentNode = this.nodeIdMap[segments[0]];
      if (!currentNode) return "";

      let path = [currentNode.name || segments[0]];

      for (let i = 1; i < segments.length; i++) {
        const segment = segments[i];
        if (segment.startsWith("[") && segment.endsWith("]")) {
          const index = parseInt(segment.slice(1, -1), 10);
          if (currentNode.subcategories && currentNode.subcategories[index]) {
            currentNode = currentNode.subcategories[index];
            path.push(currentNode.name || `Unnamed[${index}]`);
          } else {
            return "";
          }
        } else {
          if (currentNode[segment]) {
            currentNode = currentNode[segment];
            path.push(currentNode.name || segment);
          } else {
            return "";
          }
        }
      }
      return path.join("/");
    },
    saveFilterName(oldName) {
      const newName = (this.editableFilterNames[oldName] || "").trim();
      if (!newName) {
        this.editableFilterNames[oldName] = oldName;
        this.editingFilterName = null;
        return;
      }
      if (newName !== oldName && !this.editableFilters[newName]) {
        
        this.editableFilters[newName] = this.editableFilters[oldName];
        delete this.editableFilters[oldName];
        this.editableFilterNames[newName] = newName;
        delete this.editableFilterNames[oldName];

        
        const expanded = this.expandedFilters.indexOf(oldName);
        if (expanded !== -1) {
          this.expandedFilters.splice(expanded, 1, newName);
        }

        
        if (this.selectedNode.filters[oldName]) {
          this.selectedNode.filters[newName] =
            this.selectedNode.filters[oldName];
          delete this.selectedNode.filters[oldName];
        }

        
        const attrId = this.findAttributeIdByDisplayName(oldName);
        if (attrId !== null && this.attributeData[attrId]) {
          this.attributeData[attrId].display_name = newName;
        }
        this.gatherAllFilters();
      } else {
        
        this.editableFilterNames[oldName] = oldName;
      }
      this.editingFilterName = null;
    },
    startEditingValue(filterName, index) {
      
      if (this.currentView === "attributeManagement") {
        return;
      }
      this.editingValue = { filter: filterName, index };
    },
    stopEditingValue() {
      this.editingValue = { filter: null, index: null };
    },
    openValueNotesDialog(attrId, value_en) {
      const attr = this.attributeData[attrId];
      if (!attr || !attr.values) {
        console.error(`Attribute #${attrId} or its values are not defined`);
        return;
      }

      
      const valueKey = Object.keys(attr.values).find(
        (key) => attr.values[key].value_en === value_en
      );
      if (!valueKey) {
        console.error(`Value "${value_en}" not found in attribute #${attrId}`);
        return;
      }

      const valueObj = attr.values[valueKey];
      this.valueNotesDialog.attrId = attrId;
      this.valueNotesDialog.value = value_en;
      this.valueNotesDialog.valueKey = valueKey; 
      this.valueNotesDialog.tempNote = valueObj.notes || ""; 
      this.valueNotesDialog.show = true;
    },
    saveValueNotes() {
      const { attrId, valueKey, tempNote } = this.valueNotesDialog;
      if (!attrId || !valueKey) {
        console.error("Invalid attribute ID or value key");
        return;
      }

      const attr = this.attributeData[attrId];
      if (attr && attr.values && attr.values[valueKey]) {
        attr.values[valueKey].notes = tempNote; 
      } else {
        console.error(
          `Cannot save note: value #${valueKey} not found in attribute #${attrId}`
        );
      }
      this.valueNotesDialog.show = false; 
    },
    async saveChanges() {
      
      if (this.selectedNode && this.editableFilters && this.selectedNodeId) {
        for (const [fName, valArray] of Object.entries(this.editableFilters)) {
          const attrId = this.findAttributeIdByDisplayName(fName);
          if (attrId !== null && this.attributeData[attrId]) {
            const attr = this.attributeData[attrId];
            const originalValues = attr.values || {};

            
            const existingValuesMap = {};
            for (const [key, valueObj] of Object.entries(originalValues)) {
              if (valueObj.value_en) {
                existingValuesMap[valueObj.value_en] = { key, valueObj };
              }
            }

            
            const newValues = {};
            valArray.forEach((valEn) => {
              if (existingValuesMap[valEn]) {
                
                const { key, valueObj } = existingValuesMap[valEn];
                newValues[key] = { ...valueObj };
              } else {
                
                const newValId = this.nextValueId(attr);
                const maxIndex = Math.max(
                  ...Object.values(originalValues).map(
                    (v) => v.value_sort_index || 0
                  ),
                  -1
                );
                newValues[newValId] = {
                  value_en: valEn,
                  value_de: "",
                  value_fr: "",
                  value_it: "",
                  value_sort_index: maxIndex + 1,
                  notes: "",
                  sources: [],
                };
              }
            });

            
            attr.values = newValues;

            
            attr.valuesArray = this.getValuesArray(attr);
          }
        }
      }

      
      if (!this.selectedTaxonomy) {
        this.showSnackbar("No taxonomy selected", "warning");
        return;
      }

      
      this.normalizeDataBeforePublish();

      
      if (
        this.loadedVersion === "Working Version" ||
        this.loadedVersion === "QA Version"
      ) {
        try {
          
          if (this.loadedVersion === "Working Version") {
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const backupVersionName = `Working Version Backup ${timestamp}`;

            
            await this.saveVersionToCloud(
              backupVersionName,
              `Automatic backup of Working Version created on ${new Date().toLocaleString()}`
            );
            console.log(`Created automatic backup: ${backupVersionName}`);
          }

          
          const versionType = this.loadedVersion;
          await this.saveVersionToCloud(
            versionType,
            this.versionDescription ||
              `${versionType} updated on ${new Date().toLocaleString()}`
          );

          
          this.hasUnsavedChanges = false;
          this.showSnackbar(
            `${versionType} updated successfully` +
              (this.loadedVersion === "Working Version" ? " with backup" : ""),
            "success"
          );

          
          await this.loadTaxonomyVersions();
          this.loadVersion = versionType;
          return;
        } catch (error) {
          console.error(`Failed to update ${this.loadedVersion}:`, error);
          this.showSnackbar(
            `Failed to update ${this.loadedVersion}: ${error.message}`,
            "error"
          );
          return;
        }
      }

      
      const version = this.saveVersion.trim();
      if (version) {
        
        if (
          ["production", "test", "Working Version", "QA Version"].includes(
            version
          )
        ) {
          if (
            version.toLowerCase() === "production" ||
            version.toLowerCase() === "test"
          ) {
            this.showSnackbar(
              'Cannot save directly to "production" or "test". Use "Publish Taxonomy" instead.',
              "error"
            );
            return;
          } else if (version === "Working Version") {
            this.showSnackbar(
              "To update Working Version, load it first then use Save Changes",
              "info"
            );
            return;
          } else if (version === "QA Version") {
            this.showSnackbar(
              "To update QA Version, use Set Working Version or load it first",
              "info"
            );
            return;
          }
        }

        const validation = this.validateVersionName(version);
        if (!validation.valid) {
          this.showSnackbar(validation.message, "error");
          return;
        }
      }

      
      
      const suffix = version || new Date().toISOString().split("T")[0];
      const baseFilename = this.selectedTaxonomy.replace(".json", "");
      const pathModified = `allium-ai-manor/taxonomies/${baseFilename}_modified_${suffix}.json`;
      const pathAttributes = `allium-ai-manor/taxonomies/${baseFilename}_attributes_${suffix}.json`;

      
      const metadata = {
        version: suffix,
        description:
          this.versionDescription ||
          `Changes saved on ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
      };

      const payloadMod = {
        path: pathModified,
        data: this.taxonomyData,
        metadata: metadata,
      };

      const payloadAtt = {
        path: pathAttributes,
        data: this.attributeData,
        metadata: metadata,
      };

      try {
        const [resMod, resAtt] = await Promise.all([
          fetch(`${this.FUNCTION_URL}?action=save`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadMod),
          }),
          fetch(`${this.FUNCTION_URL}?action=save`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadAtt),
          }),
        ]);

        if (!resMod.ok || !resAtt.ok) {
          throw new Error(
            `Save failed with statuses ${resMod.status}, ${resAtt.status}`
          );
        }

        await Promise.all([resMod.json(), resAtt.json()]);

        this.hasUnsavedChanges = false;
        this.loadedVersion = suffix; 
        this.showSnackbar(
          "Taxonomy + Attributes saved successfully",
          "success"
        );
        await this.loadTaxonomyVersions(); 
        this.loadVersion = suffix; 
      } catch (error) {
        console.error("Save failed:", error);
        this.showSnackbar("Failed to save taxonomy", "error");
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
      setTimeout(() => {
        this.snackbar.show = false;
      }, 2000);
    },
    
    filteredAllFilterNames() {
      const query = (this.addExistingFilterName || "").toLowerCase().trim();
      if (!query) return this.allFilterNames;

      
      const nameMatches = this.allFilterNames.filter((name) =>
        name.toLowerCase().includes(query)
      );

      
      const valueMatches = new Set();

      
      if (this.valueToFilterMap[query]) {
        this.valueToFilterMap[query].forEach((filterName) => {
          valueMatches.add(filterName);
        });
      }

      
      for (const [valueLower, filterNames] of Object.entries(
        this.valueToFilterMap
      )) {
        if (valueLower.includes(query)) {
          filterNames.forEach((filterName) => {
            valueMatches.add(filterName);
          });
        }
      }

      
      const allMatches = new Set([...nameMatches]);
      valueMatches.forEach((match) => allMatches.add(match));

      return Array.from(allMatches).sort();
    },
    
    gatherAllFilters() {
      this.allFiltersMap = {};
      this.filterIdsByName = {};
      const nameCounts = {};

      for (const [attrId, attObj] of Object.entries(this.attributeData)) {
        let baseName = attObj.name || `Attribute ${attrId}`;
        let displayName = this.getDisplayName(attObj);

        
        let fName =
          displayName === baseName
            ? displayName
            : `${displayName} [${baseName}]`;

        
        if (nameCounts[fName]) {
          nameCounts[fName]++;
          fName = `${fName} (ID: ${attrId})`;
        } else {
          nameCounts[fName] = 1;
        }

        this.filterIdsByName[fName] = Number(attrId);
        if (!this.allFiltersMap[fName]) {
          this.allFiltersMap[fName] = new Set();
        }
      }

      this.allFilterNames = Object.keys(this.allFiltersMap).sort();
    },
    collectFiltersRecursively(node) {
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids.forEach((attrId) => {
          const attObj = this.attributeData[attrId];
          if (attObj) {
            const fName = attObj.display_name;
            if (!this.allFiltersMap[fName]) {
              this.allFiltersMap[fName] = new Set();
            }
            getValuesArray(attObj).forEach((valObj) =>
              this.allFiltersMap[fName].add(valObj.value_en)
            );
          }
        });
      }
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) =>
          this.collectFiltersRecursively(sub)
        );
      } else {
        const keys = Object.keys(node).filter(
          (k) =>
            !this.keysToSkip.includes(k) &&
            k !== "filters" &&
            k !== "name" &&
            k !== "subcategories" &&
            k !== "sources"
        );
        keys.forEach((k) => {
          const child = node[k];
          if (child && typeof child === "object") {
            this.collectFiltersRecursively(child);
          }
        });
      }
    },
    onExistingFilterChange(selectedFilter) {
      console.log("Selected filter:", selectedFilter);
      if (!selectedFilter) return;
      this.addExistingFilterName = selectedFilter.trim();
      this.addExistingFilter();
    },
    addExistingFilter() {
      const selectedName =
        this.addExistingFilterName && this.addExistingFilterName.trim();
      if (!selectedName) return;

      const attrId = this.filterIdsByName[selectedName];
      if (!attrId) {
        this.showSnackbar(
          `Could not find attribute for "${selectedName}"`,
          "error"
        );
        return;
      }

      if (!this.selectedNodeId) {
        this.showSnackbar("Please select a node first", "error");
        return;
      }

      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) {
        this.showSnackbar("Selected node not found", "error");
        return;
      }

      
      if (!Array.isArray(nodeData.filters)) {
        nodeData.filters = [];
      }
      if (!Array.isArray(nodeData.attribute_ids)) {
        nodeData.attribute_ids = [];
      }

      
      if (nodeData.filters.includes(attrId)) {
        const attrName = this.attributeData[attrId].name || `#${attrId}`;
        this.showSnackbar(
          `Filter "${attrName}" already exists on this node`,
          "warning"
        );
        return;
      }

      
      nodeData.filters.push(attrId);
      nodeData.attribute_ids.push(attrId);

      
      const attrObj = this.attributeData[attrId];
      if (attrObj) {
        if (!attrObj.categories) attrObj.categories = [];
        const nodePath = this.getNodePathById(this.selectedNodeId);
        if (nodePath && !attrObj.categories.includes(nodePath)) {
          attrObj.categories.push(nodePath);
        }
      }

      this.refreshSelectedNodeFilters();

      const treeItem = this.findTreeItemById(
        this.treeItems[0],
        this.selectedNodeId
      );
      if (treeItem) {
        treeItem.filters = nodeData.filters;
        if (treeItem.filters && treeItem.filters.length > 0) {
          treeItem.style = { background: "#108ffe", color: "#fff" };
        }
      }

      const attrName = this.attributeData[attrId].name || `#${attrId}`;
      this.addExistingFilterName = "";
      this.showSnackbar(`Added filter "${attrName}" to node`, "success");
    },
    startDrag(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startWidth = this.leftPaneWidth;
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.startX;
      this.leftPaneWidth = Math.max(300, this.startWidth + delta);
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
    createAttributeWithName() {
      const name = this.newAttributeName.trim();
      if (!name) {
        this.showSnackbar("Attribute name cannot be empty", "error");
        return;
      }
      if (!this.isAttributeNameUnique(name)) {
        this.showSnackbar(`Attribute name "${name}" already exists`, "error");
        return;
      }
      this.createNewAttribute(name);
      this.nameDialog = false;
    },
    createNewAttribute(customName) {
      
      const newId = this.getNextAvailableAttributeId();
      if (!newId) {
        this.showSnackbar("Failed to generate a unique attribute ID", "error");
        return null;
      }

      
      const attributeName =
        typeof customName === "string" && customName.trim()
          ? customName.trim()
          : `Attribute ${newId}`;

      
      this.attributeData[newId] = {
        name: attributeName,
        display_name: { en: attributeName, de: "", fr: "", it: "" },
        description: "",
        data_type: "text",
        always_required: false,
        is_image_attribute: false,
        is_closed_list: false,
        filter_attribute: false,
        critical_attribute: false,
        secondary_allowed: false,
        customer_facing: true,
        notes: "",
        sort_order: "Alphabetical",
        attribute_level: "Product",
        attribute_unit: "",
        justCreated: true,
        attribute_id: newId,
        is_category_type_attribute: false,
        attribute_group: null,
        values: {},
        historicalValueIds: [], 
        translations: {
          value_de: [],
          value_fr: [],
          value_it: [],
        },
        sources: [],
      };

      
      if (!this.taxonomyData._metadata) {
        this.taxonomyData._metadata = {};
      }
      if (!this.taxonomyData._metadata.historicalAttributeIds) {
        this.taxonomyData._metadata.historicalAttributeIds = [];
      }
      this.taxonomyData._metadata.historicalAttributeIds.push(newId);

      this.gatherAllFilters();
      this.showSnackbar(`Created new attribute #${newId}`, "success");
      return this.attributeData[newId];
    },
    updateItemValues(item) {
      
      item.valuesArray.forEach((vo, idx) => {
        
        
        const id = Object.keys(item.values).find(
          (k) => item.values[k].value_en === vo.value_en
        );

        
        if (id) {
          item.values[id].value_sort_index = idx;
          
          this.hasUnsavedChanges = true;
        }
      });
    },
    openTextEditDialog(attributeObj, fieldName) {
      this.textEditDialog.attributeObj = attributeObj;
      this.textEditDialog.fieldName = fieldName;
      if (fieldName === "display_name") {
        this.textEditDialog.tempValue = attributeObj.display_name?.en || "";
      } else {
        this.textEditDialog.tempValue = attributeObj[fieldName] || "";
      }
      this.textEditDialog.show = true;
    },
    saveTextEdit() {
      if (!this.textEditDialog.attributeObj || !this.textEditDialog.fieldName) {
        this.textEditDialog.show = false;
        return;
      }
      const field = this.textEditDialog.fieldName;
      const val = this.textEditDialog.tempValue;
      const attrId = this.textEditDialog.attributeObj.attribute_id;
      if (field === "display_name") {
        let displayName = this.attributeData[attrId].display_name;
        if (typeof displayName === "string") {
          
          this.attributeData[attrId].display_name = { en: displayName };
        } else if (!displayName) {
          
          this.attributeData[attrId].display_name = { en: "" };
        }
        
        this.attributeData[attrId].display_name.en = val;
      } else {
        this.attributeData[attrId][field] = val;
      }
      this.textEditDialog.show = false;
    },
    
    shouldDisplayValue(item, index) {
      return this.expandedAttributeValues[item.attribute_id] || index < 7;
    },
    
    toggleExpandValues(item) {
      const attrId = item.attribute_id;
      this.expandedAttributeValues[attrId] =
        !this.expandedAttributeValues[attrId];
    },
    
    toggleAttributeEditorFullscreen() {
      this.isAttributeEditorFullscreen = !this.isAttributeEditorFullscreen;
    },
    deleteAttribute(item) {
      const attrId = item.attribute_id;

      
      delete this.attributeData[attrId];

      
      this.removeAttributeIdFromAllTopLevels(
        this.taxonomyData,
        attrId,
        this.keysToSkip
      );

      
      this.loadTopLevelNode();

      
      if (this.selectedNodeId) {
        this.refreshSelectedNodeFilters();
      }

      
      this.gatherAllFilters();

      
      this.showSnackbar(`Attribute #${attrId} deleted`, "success");
    },
    removeAttributeIdEverywhere(obj, attrId, keysToSkip = []) {
      if (!obj || typeof obj !== "object") return;

      
      if (Array.isArray(obj)) {
        for (const element of obj) {
          this.removeAttributeIdEverywhere(element, attrId, keysToSkip);
        }
        return;
      }

      
      if (Array.isArray(obj.filters)) {
        obj.filters = obj.filters.filter(
          (id) => id !== attrId && parseInt(id) !== parseInt(attrId)
        );
      }

      if (Array.isArray(obj.attribute_ids)) {
        obj.attribute_ids = obj.attribute_ids.filter(
          (id) => id !== attrId && parseInt(id) !== parseInt(attrId)
        );
      }

      
      if (Array.isArray(obj.subcategories)) {
        for (const sub of obj.subcategories) {
          this.removeAttributeIdEverywhere(sub, attrId, keysToSkip);
        }
      }

      
      for (const [key, value] of Object.entries(obj)) {
        if (
          keysToSkip.includes(key) ||
          key === "filters" ||
          key === "attribute_ids" ||
          key === "subcategories" ||
          key === "sources" ||
          key === "name"
        ) {
          continue;
        }
        this.removeAttributeIdEverywhere(value, attrId, keysToSkip);
      }
    },
    refreshSelectedNodeFilters() {
      if (!this.selectedNodeId) {
        this.selectedNode = null;
        return;
      }
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) {
        this.selectedNode = null;
        return;
      }
      const filterIds = Array.isArray(nodeData.filters) ? nodeData.filters : [];
      const name = nodeData.name || "(untitled node)";
      const filtersDict = {};

      filterIds.forEach((attrId) => {
        const attrObj = this.attributeData[attrId];
        if (!attrObj) {
          filtersDict[`Unknown #${attrId}`] = [];
        } else {
          
          const displayName = this.getDisplayName(attrObj);
          const internalName = attrObj.name || "";

          
          const filterKey =
            displayName === internalName
              ? displayName
              : `${displayName} [${internalName}]`;

          filtersDict[filterKey] = this.getValuesArray(attrObj).map(
            (vo) => vo.value_en
          );
        }
      });

      this.selectedNode = { name, filters: filtersDict };
      this.editableFilters = JSON.parse(JSON.stringify(filtersDict));
      this.editableFilterNames = Object.fromEntries(
        Object.keys(filtersDict).map((n) => [n, n])
      );
      this.expandedFilters = this.expandedFilters.filter(
        (f) => !!filtersDict[f]
      );
    },
    exportAttributesToCSV() {
      const rows = this.filteredAttributes;
      const escapeCSV = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const headers = [
        "attribute_id",
        "name",
        "display_name",
        "description",
        "values",
        "data_type",
        "always_required",
        "is_image_attribute",
        "is_closed_list",
        "filter_attribute",
        "critical_attribute",
        "secondary_allowed",
        "customer_facing",
        "notes",
        "sort_order",
        "attribute_level",
        "attribute_unit",
        "categories",
        "filter_sources",
        "value_sources",
        "value_notes",
        "translations",
      ];

      let csv = headers.map(escapeCSV).join(",") + "\n";

      rows.forEach((item) => {
        csv +=
          [
            item.attribute_id,
            item.name,
            this.getDisplayName(item), 
            item.description,
            (this.getValuesArray(item) || []) 
              .map((v) => v.value_en)
              .join(";"),
            item.data_type,
            item.always_required ? "true" : "false",
            item.is_image_attribute ? "true" : "false",
            item.is_closed_list ? "true" : "false",
            item.filter_attribute ? "true" : "false",
            item.critical_attribute ? "true" : "false",
            item.secondary_allowed ? "true" : "false",
            item.customer_facing ? "true" : "false",
            item.notes,
            item.sort_order,
            item.attribute_level,
            item.attribute_unit,
            (item.categories || []).join(";"),
            (item.filter_sources || []).join(";"),
            JSON.stringify(item.value_sources || {}),
            JSON.stringify(item.value_notes || {}),
            JSON.stringify(item.translations || {}),
          ]
            .map(escapeCSV)
            .join(",") + "\n";
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attribute_editor_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      this.showSnackbar("CSV exported successfully!", "success");
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
      setTimeout(() => {
        this.snackbar.show = false;
      }, 2000);
    },
    goToAttributeEditorByName(name) {
      this.searchQuery = name;
      this.currentView = "attributeEditor";
    },
    deleteAttributesByName(name) {
      const toDelete = [];
      for (const [idStr, attrObj] of Object.entries(this.attributeData)) {
        if ((attrObj.name || "(unnamed)") === name) {
          toDelete.push(parseInt(idStr, 10));
        }
      }
      for (const attrId of toDelete) {
        if (this.attributeData[attrId]) {
          this.deleteAttribute({ attribute_id: attrId });
        }
      }
    },
    onFilterDragStart(event, filterName) {
      const attrId = this.findAttributeIdByDisplayName(filterName);
      if (attrId !== null) {
        event.dataTransfer.setData("filterId", attrId);
      }
    },
    onFilterDrop(event, node) {
      const filterId = event.dataTransfer.getData("filterId");
      if (!filterId) return;
      const numericFilterId = parseInt(filterId, 10);

      const nodeData = this.nodeIdMap[node.id];
      if (!nodeData) return;

      
      if (!Array.isArray(nodeData.filters)) {
        nodeData.filters = [];
      }
      if (!Array.isArray(nodeData.attribute_ids)) {
        nodeData.attribute_ids = [];
      }

      
      if (
        nodeData.filters.some((id) => String(id) === String(numericFilterId))
      ) {
        this.showSnackbar("Filter already exists on this node", "warning");
      } else {
        
        if (!nodeData.filters.includes(numericFilterId)) {
          if (!nodeData.attribute_ids) {
            nodeData.attribute_ids = [];
          }
          if (
            !nodeData.attribute_ids.some(
              (id) => String(id) === String(numericFilterId)
            )
          ) {
            nodeData.attribute_ids.push(numericFilterId);
          }
          if (!nodeData.filters) {
            nodeData.filters = [];
          }
          if (!nodeData.filters.includes(numericFilterId)) {
            nodeData.filters.push(numericFilterId);
          }
        }
        if (Array.isArray(nodeData.attribute_ids)) {
          if (
            !nodeData.filters.some(
              (id) => String(id) === String(numericFilterId)
            )
          ) {
            nodeData.filters.push(numericFilterId);
          }
        } else {
          nodeData.attribute_ids = [numericFilterId];
        }
        if (this.selectedNodeId === node.id) {
          this.refreshSelectedNodeFilters();
        }
        this.showSnackbar("Filter added to node", "success");
      }
    },
    applyExpansionState(node) {
      
      console.log("Node ID:", node.id, "Filters:", node.filters);
      if (this.expandedNodes.size === 0) {
        
        node.expand = this.collapsable ? false : true;
      } else {
        node.expand = this.expandedNodes.has(node.id);
      }
      if (node.children && node.children.length) {
        node.children = node.children.map((c) => this.applyExpansionState(c));
      }
      return node;
    },
    collectExpansions(node) {
      if (node.expand) {
        this.expandedNodes.add(node.id);
      }
      if (node.children) {
        node.children.forEach((child) => this.collectExpansions(child));
      }
    },
    openTranslationsDialog(item) {
      const attrId = item.attribute_id;
      const attr = this.attributeData[attrId];
      if (!attr) return;

      
      this.translationsDialog.item = JSON.parse(
        JSON.stringify({
          attribute_id: attrId,
          display_name: {
            en: this.getDisplayName(attr),
            de: attr.display_name?.de || "",
            fr: attr.display_name?.fr || "",
            it: attr.display_name?.it || "",
          },
          valuesArray: this.getValuesArray(attr),
        })
      );
      this.translationsDialog.show = true;
    },
    closeTranslationsDialog() {
      if (this.translationsDialog.item) {
        const attrId = this.translationsDialog.item.attribute_id;
        const attr = this.attributeData[attrId];
        if (attr) {
          
          attr.display_name = { ...this.translationsDialog.item.display_name };

          
          this.translationsDialog.item.valuesArray.forEach((vo, index) => {
            const originalValue = attr.valuesArray[index];
            if (originalValue) {
              
              originalValue.value_de = vo.value_de;
              originalValue.value_fr = vo.value_fr;
              originalValue.value_it = vo.value_it;

              
              
              const valueKey = Object.keys(attr.values).find(
                (k) => attr.values[k].value_en === originalValue.value_en
              );

              if (valueKey) {
                attr.values[valueKey].value_de = vo.value_de;
                attr.values[valueKey].value_fr = vo.value_fr;
                attr.values[valueKey].value_it = vo.value_it;
              }
            }
          });

          
          this.hasUnsavedChanges = true;

          
          this.showSnackbar(
            "Translations updated! Remember to save changes.",
            "success"
          );
        }
      }
      this.translationsDialog.show = false;
    },
  },
  watch: {
    selectedTaxonomy(newTaxonomy) {
      if (!newTaxonomy) return;
      this.versions = [];
      console.log("Versions:", this.versions);
      this.loadVersion = "";
      this.loadTaxonomyVersions().then((vers) => {
        if (vers.length > 0) {
          
          const workingVersionIndex = vers.findIndex(
            (v) => v === "Working Version"
          );
          if (workingVersionIndex !== -1) {
            this.loadVersion = "Working Version";
          } else {
            this.loadVersion = vers[0];
          }
        }
        this.loadTaxonomyFromCloud(this.loadVersion);
      });
    },
    selectedTopLevelNode() {
      this.loadTopLevelNode();
    },
    
    loadVersion(newVal) {
      console.log("loadVersion changed to:", newVal);
      console.log("Type of loadVersion:", typeof newVal);
      if (typeof newVal === "object" && newVal !== null) {
        console.log(
          "loadVersion is an object with properties:",
          Object.keys(newVal)
        );
        console.log("loadVersion contents:", JSON.stringify(newVal));
      }
    },
    
    taxonomyData: {
      deep: true,
      handler() {
        if (this.trackChanges && this.loadedVersion !== null) {
          this.hasUnsavedChanges = true;
        }
      },
    },
    attributeData: {
      deep: true,
      handler() {
        if (this.trackChanges && this.loadedVersion !== null) {
          this.hasUnsavedChanges = true;
        }
      },
    },
  },
  mounted() {
    this.loadTaxonomyList();
  },
};